import * as trace from 'owa-trace';
import commonStrings from 'owa-strings/owa-mini-common-strings';
import EventScope from 'owa-service/lib/contract/EventScope';
import React, { useContext, useRef, useState } from 'react';
import RsvpForm from './RsvpForm';
import { BottomSheet } from 'mini-bottom-sheet-route';
import { Button, ButtonType } from 'mini-button';
import { CalendarEvent } from 'mini-calendar-event-types';
import { DateRange } from 'mini-datetime-utils/lib/DateRange';
import { defaultGetCalendarEventsCallback } from '../utils/defaultGetCalendarEventsCallback';
import { defaultGetConflictsText } from '../utils/defaultGetConflictsText';
import { enterKeyHandler } from 'window-utils/lib/keyHandlerUtils';
import { format } from 'owa-strings';
import { getDefaultMiniColorScheme } from 'mini-color-utils/lib/colorUtils';
import { getNameOrAddressFromEmailAddressWrapper } from 'mini-common-utils/lib/utils/getNameOrAddressFromEmailAddressWrapper';
import { getResponseTypeFromAction } from '../utils/getResponseTypeFromAction';
import { ReactAppCtx } from 'mini-app-context';
import { ReactAppSourceContext } from 'data/lib/utils/ReactAppSourceContext';
import { RsvpAction } from '../schema/RsvpAction';
import { RsvpButtonProps } from '../schema/RsvpButton.types';
import { RsvpMaybeButton, RsvpNoButton, RsvpYesButton } from './RsvpActionButtons';
import { RsvpRemoveButton } from './RsvpRemoveButton';
import { RsvpServiceState } from '../schema/RsvpServiceState';
import { RsvpStringProps } from '../schema/RsvpString.types';

import {
    MeetingResponseType,
    respondToMeeting,
    throwResponseError,
} from 'mini-calendar-event-services';
import {
    RSVP_OVERLAY_CLICKED,
    RSVP_EVENT_COMPLETE,
    RSVP_EVENT_CLICKED,
} from 'data/lib/mini-app-constants/commonEventSignalConstants';

const MAX_CHARS_ALLOWED_FOR_RSVP_NOTES: number = 500;

const RsvpButton: React.FC<RsvpButtonProps> = props => {
    // get the mandatory props
    const { item } = props;

    const styles = require('./RsvpButton.scss');
    const classNames = require('classnames/bind').bind(styles);

    // create the default props
    const defaultColorScheme = getDefaultMiniColorScheme();

    const defaultOnRsvpButtonClicked = (
        item: CalendarEvent,
        onFetchEventScope: (fetchedScope: EventScope) => void
    ) => onFetchEventScope(EventScope.Default);

    const defaultGetCalendarEvents = defaultGetCalendarEventsCallback(item.ItemId?.mailboxInfo);

    const defaultGetterForConflictsText = () => defaultGetConflictsText(item);

    // get the default and optional props
    const {
        colorScheme = defaultColorScheme,
        onRsvpButtonClicked = defaultOnRsvpButtonClicked,
        getConflictsText = defaultGetterForConflictsText,
        getCalendarEvents = defaultGetCalendarEvents,
        onRsvpActionServiceCompletion,
        showRsvpResponseIfApplicable,
        getAriaLabel,
        shouldDeleteMailItemFromServer = false,
        tabIndex,
    } = props;

    // get the strings
    const stringProps: RsvpStringProps = {
        emailOrganizer: commonStrings.emailOrganizer,
        messagePlaceholder: format(
            commonStrings.composeMessagePlaceholder,
            getNameOrAddressFromEmailAddressWrapper(item?.Organizer?.Mailbox)
        ),
        yesButtonTitle: commonStrings.rsvpYes,
        maybeButtonTitle: commonStrings.rsvpMaybe,
        noButtonTitle: commonStrings.rsvpNo,
        yesButtonAriaTitle: commonStrings.rsvpYesAriaLabel,
        maybeButtonAriaTitle: commonStrings.rsvpMaybeAriaLabel,
        noButtonAriaTitle: commonStrings.rsvpNoAriaLabel,
    };

    // refs
    const buttonRef = useRef(null);

    // get the action source context value
    const actionSource = useContext(ReactAppSourceContext);
    const ctx = useContext(ReactAppCtx);

    const { ResponseType } = item;

    // Button props
    let actionButtonType = ButtonType.Inline;
    let actionButtonText: string;
    let actionButtonAriaLabel: string;

    // state
    const [showRsvpForm, setShowRsvpForm] = useState<boolean>(false);
    const [chosenScope, setChosenScope] = useState<EventScope>(EventScope.Default);

    const onOverlayClick = () => {
        setShowRsvpForm(false);
        ctx?.signal(RSVP_OVERLAY_CLICKED, { isUIAction: true }, { actionSource });
    };
    const onRSVPActionCallback = (
        actionType: RsvpAction,
        updateOrganizer: boolean,
        message: string,
        proposedNewTime?: DateRange
    ) => {
        // determine the type of response from the action
        const responseType: MeetingResponseType = getResponseTypeFromAction(
            actionType,
            item.IsOrganizer
        );

        const onRsvpCompletion = (completionState: RsvpServiceState) =>
            onRsvpActionServiceCompletion?.(
                completionState,
                actionType,
                updateOrganizer,
                message,
                chosenScope,
                proposedNewTime
            );

        // Invoke the service
        respondToMeeting({
            item,
            response: responseType,
            sendResponse: updateOrganizer,
            scope: chosenScope,
            responseMessage: message,
            proposedStartTime: proposedNewTime?.start,
            proposedEndTime: proposedNewTime?.end,
            shouldDeleteMailItemFromServer: shouldDeleteMailItemFromServer,
        })
            .then(responses => {
                let isSuccess = responses.length > 0;
                let failedResponse = null;
                responses.map(response => {
                    if (response?.ResponseClass !== 'Success') {
                        failedResponse = response;
                        isSuccess = false;
                    }
                });
                if (isSuccess) {
                    onRsvpCompletion(RsvpServiceState.Success);
                } else {
                    throwResponseError(failedResponse);
                }
                ctx?.signal(
                    RSVP_EVENT_COMPLETE,
                    undefined,
                    { actionSource },
                    isSuccess
                        ? undefined
                        : {
                              error: new Error('[RsvpButton]: ' + failedResponse.ResponseCode),
                          }
                );
            })
            .catch(error => {
                trace.errorThatWillCauseAlert(error);
                onRsvpCompletion(RsvpServiceState.Failed);
                ctx?.signal(RSVP_EVENT_COMPLETE, undefined, { actionSource }, { error });
            });

        setShowRsvpForm(false);
    };

    const onClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
        ev.stopPropagation();
        ctx?.signal(RSVP_EVENT_CLICKED, { isUIAction: true }, { actionSource });
        onRsvpButtonClicked?.(item, (scope: EventScope) => {
            setChosenScope(scope);
            setShowRsvpForm(true);
        });
    };

    const showRespondedRsvpButton =
        showRsvpResponseIfApplicable &&
        !item.canRemove() &&
        ['Accept', 'Tentative', 'Decline'].includes(ResponseType);

    let finalButton;
    const conflictsCount = item.ActualConflictingMeetingCount;

    if (showRespondedRsvpButton) {
        switch (ResponseType) {
            case 'Accept':
                finalButton = <RsvpYesButton onAction={onClick} />;
                break;
            case 'Tentative':
                finalButton = <RsvpMaybeButton onAction={onClick} />;
                break;
            case 'Decline':
                finalButton = <RsvpNoButton onAction={onClick} />;
                break;
        }
    } else if (item?.canRemove()) {
        finalButton = <RsvpRemoveButton {...props} />;
    } else {
        switch (ResponseType) {
            case 'Accept':
                // it means we have opted to not show rsvp response status on the button
                actionButtonText = commonStrings.editRsvpButtonLabel;
                actionButtonAriaLabel = commonStrings.editRSVPAcceptedButtonAriaLabel;
                break;
            case 'Tentative':
                // it means we have opted to not show rsvp response status on the button
                actionButtonText = commonStrings.editRsvpButtonLabel;
                actionButtonAriaLabel = commonStrings.editRSVPTentativeButtonAriaLabel;
                break;

            default:
                // rsvp case
                actionButtonAriaLabel = format(commonStrings.rsvpButtonAriaLabel, conflictsCount);
                actionButtonText = commonStrings.rsvpButtonLabel;
                actionButtonType = ButtonType.InlinePrimary;
                break;
        }

        finalButton = (
            <Button
                hasPopup={!item?.IsOrganizer}
                text={actionButtonText}
                type={actionButtonType}
                colorScheme={colorScheme}
                onKeyDown={enterKeyHandler(onClick)}
                onClick={onClick}
                tabIndex={tabIndex}
                ariaLabel={getAriaLabel?.(actionButtonAriaLabel) || actionButtonAriaLabel}
                ref={buttonRef}
                onTouchStart={ev => {
                    ev.stopPropagation();
                }}
                onTouchEnd={ev => {
                    ev.stopPropagation();
                }}
                className={classNames(styles.button)}
            />
        );
    }

    return (
        <>
            {finalButton}
            <BottomSheet
                id="RsvpForm"
                open={showRsvpForm}
                onOverlayClick={onOverlayClick}
                elementToFocusOnClose={buttonRef}>
                <ReactAppCtx.Provider value={ctx}>
                    <ReactAppSourceContext.Provider value={actionSource}>
                        <RsvpForm
                            item={item}
                            getConflictsText={getConflictsText}
                            getCalendarEvents={getCalendarEvents}
                            onAction={onRSVPActionCallback}
                            isMessageToOrganizerAllowed={item.IsResponseRequested}
                            defaultValueUpdateOrganizerSetting={item.IsResponseRequested}
                            maxCharsAllowed={MAX_CHARS_ALLOWED_FOR_RSVP_NOTES}
                            stringProps={stringProps}
                            colorScheme={colorScheme}
                        />
                    </ReactAppSourceContext.Provider>
                </ReactAppCtx.Provider>
            </BottomSheet>
        </>
    );
};

export default RsvpButton;
